import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  FaWhatsapp, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock,
  FaPaperPlane, FaCheckCircle
} from 'react-icons/fa';
import PageTransition from '../components/common/PageTransition';
import ContactInfo from '../components/contact/ContactInfo';
import SocialLinks from '../components/contact/SocialLinks';
import EnquiryButton from '../components/contact/EnquiryButton';
import { CONTACT } from '../utils/constants';
import SEO from '../components/common/SEO';

const contactCards = [
  { icon: FaWhatsapp, title: 'WhatsApp', description: 'Instant replies!', value: '+91 96183 77750', link: CONTACT.whatsappLink, external: true, color: '#25D366', bg: 'rgba(37,211,102,0.08)', border: 'rgba(37,211,102,0.2)' },
  { icon: FaPhone, title: 'Phone', description: 'Call us directly', value: '+91 96183 77750', link: `tel:${CONTACT.phone}`, external: false, color: '#1565C0', bg: 'rgba(21,101,192,0.08)', border: 'rgba(21,101,192,0.2)' },
  { icon: FaEnvelope, title: 'Email', description: 'Write to us', value: 'thetinytimes...', link: `mailto:${CONTACT.email}`, external: false, color: '#F57C00', bg: 'rgba(245,124,0,0.08)', border: 'rgba(245,124,0,0.2)' },
  { icon: FaClock, title: 'Hours', description: 'Mon–Sat', value: '9 AM – 6 PM', link: '#', external: false, color: '#7B2FBE', bg: 'rgba(123,47,190,0.08)', border: 'rgba(123,47,190,0.2)' },
];

const contactFaqs = [
  { icon: '⏰', question: 'What is your response time?', answer: 'We typically respond within 2–4 hours during business hours via WhatsApp, and within 24 hours for emails.' },
  { icon: '📦', question: 'How can I track my subscription?', answer: 'Simply message us on WhatsApp with your subscription details, and we\'ll provide the latest status.' },
  { icon: '💳', question: 'What payment methods do you accept?', answer: 'We accept UPI, bank transfers, and all major payment methods. Details shared upon subscription.' },
  { icon: '🔄', question: 'Can I change my subscription plan?', answer: 'Yes! Contact us via WhatsApp or email to upgrade, downgrade, or modify your subscription.' },
  { icon: '🎁', question: 'Do you offer gift subscriptions?', answer: 'Absolutely! Gift The Tiny Times to loved ones. Contact us for special gift packaging options.' },
  { icon: '🤝', question: 'How can I partner with The Tiny Times?', answer: 'We welcome partnerships! Send us an email with your proposal and we\'ll get back to you.' },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '', enquiryType: 'general' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    const message = encodeURIComponent(
      `📩 *New Enquiry from Website*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Subject:* ${formData.subject}\n*Type:* ${formData.enquiryType}\n\n*Message:*\n${formData.message}`
    );
    window.open(`https://wa.me/919618377750?text=${message}`, '_blank');
    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', enquiryType: 'general' });
    }, 3000);
  };

  return (
    <PageTransition>
      <SEO
        title="Contact Us - Customer Support & Subscriptions"
        description="Get in touch with The Tiny Times team for subscriptions, feedback, advertising, or support. We are based in Hyderabad, delivering nationwide."
        keywords="contact tiny times, kids magazine support, customer care childrens book, subscribe customer support"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact The Tiny Times",
          "description": "Get in touch with The Tiny Times team for subscriptions, feedback, advertising, or support.",
          "url": "https://the-tiny-times.web.app/contact",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-9618377750",
            "contactType": "customer service",
            "email": "thetinytimeschildrensnewspaper@gmail.com"
          }
        }}
      />
      <div className="min-h-screen">

        {/* Hero */}
        <section className="relative py-24 md:py-36 overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #EDF7EE 0%, #E3F2FD 45%, #EDE7F6 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(21,101,192,0.12) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute top-0 left-0 w-80 h-80 opacity-15 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.3), transparent 70%)', filter: 'blur(50px)', transform: 'translate(-25%,-25%)' }} />

          {[{ e: '📞', x: '8%', y: '20%' }, { e: '💬', x: '86%', y: '18%' }, { e: '✉️', x: '78%', y: '70%' }, { e: '🌟', x: '12%', y: '72%' }].map((item, i) => (
            <motion.div key={i} className="absolute text-5xl opacity-20 pointer-events-none select-none"
              style={{ left: item.x, top: item.y }}
              animate={{ y: [0, -20, 0], rotate: [0, 12, -12, 0] }}
              transition={{ duration: 4 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}>
              {item.e}
            </motion.div>
          ))}

          <div className="container mx-auto px-4 relative z-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center">
              <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }} className="text-8xl mb-5">👋</motion.div>
              <h1 className="text-5xl md:text-7xl font-display font-bold mb-5 leading-tight" style={{ color: '#1A1F2E' }}>
                Get in{' '}
                <span style={{ background: 'linear-gradient(135deg, #1565C0, #7B2FBE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Touch!
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-gray-dark leading-relaxed max-w-xl mx-auto">
                We'd love to hear from you! Questions, feedback, or just want to say hello — reach out anytime 💌
              </p>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 80" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
              <path fill="white" d="M0,40L60,45C120,50,240,60,360,55C480,50,600,30,720,28C840,25,960,35,1080,40C1200,45,1320,42,1380,40L1440,38L1440,80L0,80Z" />
            </svg>
          </div>
        </section>

        {/* Contact cards */}
        <section className="py-14 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {contactCards.map((card, index) => (
                <motion.a
                  key={index}
                  href={card.link}
                  target={card.external ? '_blank' : '_self'}
                  rel={card.external ? 'noopener noreferrer' : ''}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }} whileHover={{ y: -8, scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  className="rounded-2xl p-5 text-center cursor-pointer transition-all"
                  style={{ background: card.bg, border: `1px solid ${card.border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                >
                  <div className="w-13 h-13 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: card.color }}>
                    <card.icon className="text-2xl text-white" />
                  </div>
                  <h3 className="font-display font-bold text-base mb-1" style={{ color: card.color }}>{card.title}</h3>
                  <p className="text-xs text-neutral-gray-dark mb-1">{card.description}</p>
                  <p className="text-xs font-semibold" style={{ color: card.color }}>{card.value}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Main Contact Form + Info */}
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(160deg, #F0FFF4, #EFF6FF)' }}>
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-6xl mx-auto">

              {/* Form */}
              <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <div className="rounded-2xl p-7 md:p-9 h-full"
                  style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(16px)', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', border: '1px solid rgba(255,255,255,0.9)' }}>
                  <div className="text-center mb-7">
                    <div className="text-5xl mb-3">📝</div>
                    <h2 className="text-3xl font-display font-bold mb-1" style={{ color: '#1A1F2E' }}>Send Us a Message</h2>
                    <p className="text-neutral-gray-dark text-sm">Fill out the form and we'll get back to you via WhatsApp!</p>
                  </div>

                  {isSubmitted ? (
                    <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
                      <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ background: '#3CB044' }}>
                        <FaCheckCircle className="text-4xl text-white" />
                      </div>
                      <h3 className="text-2xl font-display font-bold mb-2" style={{ color: '#1E7C24' }}>Message Sent! 🎉</h3>
                      <p className="text-neutral-gray-dark">We'll get back to you soon via WhatsApp.</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-display font-semibold mb-1.5" style={{ color: '#1A1F2E' }}>Your Name *</label>
                          <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name"
                            className="input-field" />
                        </div>
                        <div>
                          <label className="block text-sm font-display font-semibold mb-1.5" style={{ color: '#1A1F2E' }}>Email *</label>
                          <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com"
                            className="input-field" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-display font-semibold mb-1.5" style={{ color: '#1A1F2E' }}>Phone</label>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 9876543210"
                            className="input-field" />
                        </div>
                        <div>
                          <label className="block text-sm font-display font-semibold mb-1.5" style={{ color: '#1A1F2E' }}>Enquiry Type</label>
                          <select name="enquiryType" value={formData.enquiryType} onChange={handleChange} className="input-field">
                            <option value="general">General Enquiry</option>
                            <option value="subscription">Subscription</option>
                            <option value="feedback">Feedback</option>
                            <option value="partnership">Partnership</option>
                            <option value="advertising">Advertising</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-display font-semibold mb-1.5" style={{ color: '#1A1F2E' }}>Subject *</label>
                        <input type="text" name="subject" value={formData.subject} onChange={handleChange} required placeholder="What is this about?"
                          className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-display font-semibold mb-1.5" style={{ color: '#1A1F2E' }}>Your Message *</label>
                        <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} placeholder="Type your message here..."
                          className="input-field resize-none" />
                      </div>
                      <motion.button type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full py-4 rounded-button font-display font-bold text-xl text-white flex items-center justify-center gap-3 transition-all disabled:opacity-55"
                        style={{ background: 'linear-gradient(135deg, #25D366, #1E7C24)', boxShadow: '0 6px 24px rgba(37,211,102,0.3)' }}>
                        {isSubmitting ? (
                          <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Sending...</span></>
                        ) : (
                          <><FaPaperPlane /><span>Send via WhatsApp</span></>
                        )}
                      </motion.button>
                      <p className="text-xs text-center text-neutral-gray-dark">Your message will be sent via WhatsApp 💬</p>
                    </form>
                  )}
                </div>
              </motion.div>

              {/* Contact Info */}
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                className="space-y-5">
                {/* Details card */}
                <div className="rounded-2xl p-7 shadow-card bg-white">
                  <div className="text-center mb-6">
                    <div className="text-5xl mb-3">📬</div>
                    <h2 className="text-2xl font-display font-bold mb-1" style={{ color: '#1A1F2E' }}>Contact Details</h2>
                    <p className="text-sm text-neutral-gray-dark">Reach out through any of these channels</p>
                  </div>
                  <div className="space-y-3">
                    {[
                      { href: `mailto:${CONTACT.email}`, icon: FaEnvelope, label: 'Email Us', value: CONTACT.email, color: '#F57C00', bg: '#FFF3E0' },
                      { href: `tel:${CONTACT.phone}`, icon: FaPhone, label: 'Call Us', value: CONTACT.phone, color: '#1565C0', bg: '#E3F2FD' },
                      { href: CONTACT.whatsappLink, icon: FaWhatsapp, label: 'WhatsApp', value: 'Quick response guaranteed!', color: '#25D366', bg: '#E8F5E0', external: true },
                      { href: null, icon: FaClock, label: 'Office Hours', value: 'Mon–Sat: 9:00 AM – 6:00 PM', color: '#7B2FBE', bg: '#F3E5FF' },
                    ].map((item, i) => (
                      <a key={i}
                        href={item.href || undefined}
                        target={item.external ? '_blank' : undefined}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${item.href ? 'hover:scale-[1.02] cursor-pointer' : 'cursor-default'}`}
                        style={{ background: item.bg }}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: item.color }}>
                          <item.icon className="text-xl text-white" />
                        </div>
                        <div className="overflow-hidden">
                          <div className="font-display font-bold text-sm mb-0.5" style={{ color: item.color }}>{item.label}</div>
                          <div className="text-xs text-neutral-gray-dark truncate">{item.value}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Enquiry Form CTA */}
                <EnquiryButton />

                {/* Social Links */}
                <div className="rounded-2xl p-6 shadow-card bg-white">
                  <div className="text-center mb-5">
                    <div className="text-4xl mb-2">🌐</div>
                    <h3 className="text-xl font-display font-bold" style={{ color: '#1A1F2E' }}>Follow Us</h3>
                    <p className="text-sm text-neutral-gray-dark">Stay connected on social media!</p>
                  </div>
                  <SocialLinks />
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>Common Questions ❓</h2>
              <p className="text-lg text-neutral-gray-dark max-w-xl mx-auto">Quick answers to frequently asked questions</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
              {contactFaqs.map((faq, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }} whileHover={{ y: -4 }}
                  className="rounded-2xl p-5"
                  style={{ background: 'linear-gradient(135deg, #F8FFFE, #EFF6FF)', border: '1px solid rgba(21,101,192,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <div className="flex gap-4">
                    <div className="text-3xl flex-shrink-0">{faq.icon}</div>
                    <div>
                      <h3 className="font-display font-bold text-base mb-2" style={{ color: '#1A1F2E' }}>{faq.question}</h3>
                      <p className="text-sm text-neutral-gray-dark leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Map Placeholder */}
        <section className="py-14" style={{ background: '#F5F7FA' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-7">
                <h2 className="text-3xl font-display font-bold mb-1" style={{ color: '#1A1F2E' }}>Find Us 📍</h2>
                <p className="text-neutral-gray-dark">Based in Hyderabad, India — delivering nationwide!</p>
              </div>
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                <div className="aspect-video flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #EDF7EE, #EFF6FF)' }}>
                  <div className="text-center">
                    <div className="text-8xl mb-4">🗺️</div>
                    <h3 className="text-2xl font-display font-bold mb-2" style={{ color: '#1A1F2E' }}>Hyderabad, India</h3>
                    <p className="text-neutral-gray-dark mb-5">Delivering The Tiny Times across India!</p>
                    <motion.a href="https://maps.google.com" target="_blank" rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-button font-display font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #3CB044, #1E7C24)', boxShadow: '0 4px 16px rgba(60,176,68,0.3)' }}>
                      <FaMapMarkerAlt />
                      Open in Google Maps
                    </motion.a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20" style={{ background: 'linear-gradient(135deg, #1E7C24, #145218)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
              <div className="text-7xl mb-5">💝</div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">We're Here for You!</h2>
              <p className="text-xl text-white/85 mb-8">Have questions about subscriptions, feedback, or anything else? We typically respond within 24 hours!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl text-white"
                  style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.4)' }}>
                  <FaWhatsapp className="text-2xl" /> Chat Now
                </motion.a>
                <motion.a href="/subscribe" whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-button font-display font-bold text-xl"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.3)' }}>
                  Subscribe Now 🚀
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Contact;